import React from 'react'
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Link,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import SendIcon from '@mui/icons-material/Send'

import { AutoCoderEntity } from '../../../types/gotypes'
import {
  entityDisplayName,
  formatPublishedDate,
  useEntityMetrics,
} from '../../../hooks/useEntityBrowser'
import useEntityFilterSummary from '../../../hooks/useEntityFilterSummary'
import { formatMetricValue, FormatType } from '../../social-media/metrics-config'
import { entityRecordsEmptyMessage, reportedCommentCount } from './entityRecordsEmptyMessage'
import RecordsExplorer from '../RecordsExplorer'
import PlatformAttribution from '../../social/PlatformAttribution'

/**
 * One entity, fully: what the platform says about it, every metric the job
 * holds for it, and every record that is about it — through the same
 * `RecordsExplorer` the receipt drill-down and the pipeline Results use, so a
 * comment looks the same wherever it is reached from.
 *
 * The metrics summary shows ALL of the job's metrics, not the four the table
 * has room for; a metric this entity carries no value for renders as a gap,
 * never as zero — an unmeasured video and a video measured at zero are
 * different facts.
 */

interface EntityDetailViewProps {
  customer: string
  job: string
  /** Null closes the dialog, which is what unmounts its queries. */
  entity: AutoCoderEntity | null
  onClose: () => void
  /** Offered as an action here too, so the drill-down is not a dead end. */
  onSendToPipeline?: (entity: AutoCoderEntity) => void
}

const EntityDetailView: React.FC<EntityDetailViewProps> = ({
  customer,
  job,
  entity,
  onClose,
  onSendToPipeline,
}) => {
  const open = !!entity
  const summary = useEntityFilterSummary({ customer, job, enabled: open })
  const metricKeys = summary.metrics.map(m => m.metric)
  const { metricsByEntity } = useEntityMetrics(
    customer,
    job,
    entity ? [entity.id] : [],
    metricKeys,
    open,
  )
  const metrics = entity ? metricsByEntity[entity.id] || {} : {}

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { width: '90vw', maxWidth: '90vw', height: '85vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        {entity ? entityDisplayName(entity) : ''}
        {entity && (
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.5 }}>
            {entity.platform && <PlatformAttribution platform={entity.platform} url={entity.url} />}
            {entity.kind && <Chip size="small" variant="outlined" label={entity.kind} />}
            <Typography variant="body2" color="text.secondary">
              Published {formatPublishedDate(entity.publishedDate)}
            </Typography>
            {entity.url && !entity.platform && (
              <Link
                href={entity.url}
                target="_blank"
                rel="noreferrer"
                variant="body2"
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                Open on {entity.platform || 'platform'}
                <OpenInNewIcon fontSize="inherit" />
              </Link>
            )}
          </Stack>
        )}
      </DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        {entity && (
          <>
            {summary.metrics.length > 0 && (
              <Box sx={{ mb: 2, flexShrink: 0 }}>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                  {summary.metrics.map(bound => {
                    const value = metrics[bound.metric]
                    return (
                      <Paper
                        key={bound.metric}
                        elevation={0}
                        variant="outlined"
                        sx={{ px: 2, py: 1, minWidth: 130 }}
                      >
                        <Typography variant="caption" color="text.secondary" noWrap>
                          {value?.displayName || bound.displayName || bound.metric}
                        </Typography>
                        <Typography variant="h6">
                          {/* A metric this entity carries no row for is a gap,
                              not a zero. */}
                          {value
                            ? formatMetricValue(
                                value.value,
                                (value.format || bound.format) as FormatType,
                              )
                            : '—'}
                        </Typography>
                      </Paper>
                    )
                  })}
                </Stack>
              </Box>
            )}

            <Typography variant="subtitle2" sx={{ mb: 1, flexShrink: 0 }}>
              Records about this {entity.kind || 'entity'}
            </Typography>
            <RecordsExplorer
              customer={customer}
              job={job}
              door={{ entityId: entity.id }}
              emptyMessage={entityRecordsEmptyMessage(
                entity.kind,
                reportedCommentCount(summary.metrics, metrics),
              )}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {onSendToPipeline && entity && (
          <Button
            color="info"
            startIcon={<SendIcon />}
            onClick={() => onSendToPipeline(entity)}
          >
            Send to pipeline
          </Button>
        )}
        <Button color="info" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EntityDetailView
